import type { ApiErrorResponse } from '../contract.js';

/**
 * The site talks to exactly one endpoint — POST /v1/waitlist — so this is deliberately
 * smaller than the portal's client: no tokens, no refresh, no cache.
 *
 * `||`, NOT `??`. An unset Docker build arg becomes an EMPTY STRING rather than undefined,
 * so `??` kept "" and every request went out as a RELATIVE url against the site's own
 * origin — which is how the first Cloud Run deploy silently POSTed to /waitlist on itself
 * instead of reaching the API. Empty has to fall back too.
 */
const BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8080/v1')
  // Trailing slash stripped: paths below all start with one, and this value is typed
  // by hand into a Cloud Build substitution, where ".../v1/" is an easy thing to write.
  .replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Field-level messages from Zod, so a bad email lands on the email input. */
    readonly issues: { path: string; message: string }[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function post<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(BASE + path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Could not reach Manasil. Check your connection.');
  }

  if (!res.ok) {
    const payload = (await res.json().catch(() => null)) as ApiErrorResponse | null;
    const error = payload?.error;
    const issues = (error?.details as { issues?: { path: string; message: string }[] })?.issues;
    throw new ApiError(
      res.status,
      error?.code ?? 'UNKNOWN',
      error?.message ?? 'Something went wrong. Please try again.',
      issues ?? [],
    );
  }
  return (await res.json()) as T;
}
